import { FormEvent, useState } from 'react';
import { parseInviteLink, persistRedeemedInvite, redeemInviteToken } from '../utils/inviteRedeem';

interface InviteScreenProps {
  onConnect: (url: string, token: string) => void;
  connecting: boolean;
}

export default function InviteScreen({ onConnect, connecting }: InviteScreenProps) {
  const [nickname, setNickname] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleStart = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { inviteOrigin, token, role } = parseInviteLink(inviteLink.trim());
      const data = await redeemInviteToken(inviteOrigin, token);
      const finalName = nickname.trim() || data.display_name || 'Friend';

      persistRedeemedInvite({
        token,
        inviteOrigin,
        role,
        displayName: finalName,
        allowedAgents: data.allowed_agents,
        gatewayUrl: data.gateway_url,
        gatewayToken: data.gateway_token,
      });
      onConnect(data.gateway_url, data.gateway_token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const busy = connecting || loading;

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <form
        onSubmit={handleStart}
        className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6 space-y-5"
      >
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Clawlet</h1>
          <p className="text-sm text-secondary-foreground">
            Enter your name and paste the invite link from your admin.
          </p>
        </div>

        <label className="block space-y-2">
          <span className="text-xs uppercase tracking-wide text-secondary-foreground">Your name</span>
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="e.g. Alice"
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring"
          />
        </label>

        <label className="block space-y-2">
          <span className="text-xs uppercase tracking-wide text-secondary-foreground">Invite link</span>
          <input
            type="text"
            value={inviteLink}
            onChange={(e) => setInviteLink(e.target.value)}
            placeholder="http://192.168.x.x:61424/join?token=..."
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring"
          />
        </label>

        <button
          type="submit"
          disabled={busy || !inviteLink.trim()}
          className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {busy ? (
            <>
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              Connecting...
            </>
          ) : (
            'Start →'
          )}
        </button>

        {error ? <p className="text-sm text-destructive-foreground">{error}</p> : null}
      </form>
    </div>
  );
}
